import { Observable, of } from 'rxjs'

import { ParentOrProductBySkusDocument, ParentProductFragment } from '../../graphql/search'
import { right } from '../../utils/result'
import { mapRight } from '../../utils/rx/operators'
import { commaQueryFactory, SearchQueryResult } from './query'

const parentOrProductBySkusQuery = commaQueryFactory(ParentOrProductBySkusDocument)

/**
 * Query parent product details using parent or (child) product SKUs.
 * Returns parent product details for each SKU (if found), maintaining the original order.
 */
export const searchParentOrProductBySkusQuery = ({
  skus,
}: {
  skus: string[]
}): Observable<SearchQueryResult<ParentProductFragment[]>> =>
  !skus || skus.length === 0
    ? of({ _tag: 'Right' as const, data: [] })
    : parentOrProductBySkusQuery({
        // sort skus for efficient caching
        variables: { skus: skus.slice().sort() },
      }).pipe(
        // Maintain the original order of requested SKUs by matching against parent product SKUs
        mapRight(({ data }) => {
          const parentProductNodes = data?.parentProducts?.nodes ?? []
          const productNodes = data?.products?.nodes ?? []
          return right(
            skus.reduce((acc: ParentProductFragment[], sku) => {
              // resolve parent product details from `parentProducts` or `products` query
              const parentProduct =
                parentProductNodes.find((node) => node.sku === sku) ??
                productNodes.find((node) => node.sku === sku)?.parentProduct
              if (parentProduct) {
                acc.push(parentProduct)
              }
              return acc
            }, []),
          )
        }),
      )
